// Import verb — file picker + drag-into-panel fallback. Produces an
// AudioBuffer via decodeAudioFile (shared with the drop overlay) and
// hands it to the forge shell's onBuffer.
//
// We intentionally do NOT commit here. The forge shell runs the trim
// chain and commits on the user's explicit Save click — that's the
// whole point of having a forge as opposed to the drop-overlay's
// commit-immediately flow.

import { useCallback, useRef, useState } from 'react';
import { decodeAudioFile } from '../../import';
import type { VerbProps } from '../types';

export function ImportVerb({ audioContext, onBuffer, onStatus }: VerbProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const ingest = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      if (!audioContext) {
        onStatus('Press Play once to boot the engine.', 'warn');
        return;
      }
      const file = files.item(0);
      if (!file) return;
      const decoded = await decodeAudioFile(file, audioContext);
      if (!decoded.ok) {
        onStatus(`Import rejected: ${decoded.reason}${decoded.detail ? ` (${decoded.detail})` : ''}`, 'err');
        return;
      }
      onBuffer(decoded.buffer, {
        suggestedName: decoded.suggestedName,
        origin: { kind: 'import', filename: decoded.filename },
      });
      onStatus(`Loaded ${decoded.filename} — trim and save.`, 'ok');
    },
    [audioContext, onBuffer, onStatus],
  );

  return (
    <div
      className={
        'flex flex-col items-center justify-center min-h-[200px] border-2 border-dashed rounded-lg p-10 text-center transition-colors ' +
        (dragActive
          ? 'border-yellow-300 bg-yellow-300/5'
          : 'border-zinc-700 bg-zinc-950')
      }
      onDragEnter={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        void ingest(e.dataTransfer.files);
      }}
    >
      <div className="text-zinc-300 mb-3">Drop an audio file here</div>
      <div className="text-zinc-500 text-xs mb-5">
        wav · mp3 · ogg · opus · flac · up to 8 MB, 2 min
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="px-4 py-2 bg-zinc-800 border border-zinc-700 text-zinc-200 text-xs uppercase tracking-[0.2em] hover:bg-zinc-700"
      >
        Choose file
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={(e) => void ingest(e.target.files)}
      />
    </div>
  );
}
